import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

import contentTypesConst from "astrid-distribution/src/constants/contentTypes";

import Flex from "../../../../components/Flex/Flex";
import AsyncButton from "../../../ui/components/AsyncButton/AsyncButton";
import Button from "../../../ui/components/Buttons/Button";
import ConfirmationButton from "../../../ui/components/ConfirmationButton/ConfirmationButton";
import FileDownloadModal from "../../../ui/components/FileDownload/FileDownloadModal";
import Toast from "../../../ui/components/Toast/Toast";

import ArticleDistributeModal from "../../../articles/components/ArticleDistributeModal/ArticleDistributeModal";
import Form from "../../../forms/components/Form/Form";
import Table from "../../../tables/components/Table";

import ArtifactAudioPlayer from "../../components/ArtifactAudioPlayer/ArtifactAudioPlayer";
import useArtifactFilesTableColumns from "../../hooks/useArtifactFilesTableColumns";

import ArtifactEpubReader from "../ArtifactEpubReader/ArtifactEpubReader";
import ArtifactFilesUploadForm from "../ArtifactFilesUpload/ArtifactFilesUploadForm";

import useArtifactManagerFiles from "./hooks/useArtifactManagerFiles";
import useArtifactManagerForm from "./hooks/useArtifactManagerForm";
import useArtifactManagerMarkers from "./hooks/useArtifactManagerMarkers";

export default function ArtifactManager({ files, artifact, disabled }) {
	const { t } = useTranslation();

	const { article, publisher } = useOutletContext();

	const [showUploadModal, setShowUploadModal] = useState(false);
	const [showDownloadModal, setShowDownloadModal] = useState(false);

	const { form, formState, onSubmit, onReset, deliveryModal } = useArtifactManagerForm({
		article,
		artifact,
		files,
	});

	const {
		sortedFiles,
		isFilesSorted,
		contentTypes,
		onAddFiles,
		onSortFiles,
		onReorderFiles,
		onDownloadFiles,
		onDownloadFilesAsZip,
		onDeleteFiles,
	} = useArtifactManagerFiles({
		form,
		artifact,
		files,
	});

	const { markers, onAddMarker, onUpdateMarker, onRemoveMarker, onSetFileMarkers, onSetAutoMarkers } =
		useArtifactManagerMarkers({
			form,
			artifact,
			files: sortedFiles,
		});

	const addFiles = ({ files, fileMarkers }) => {
		onAddFiles({ files });

		if (fileMarkers) {
			onSetFileMarkers(files);
		}
	};

	const columns = useArtifactFilesTableColumns({ disabled, contentTypes, onReorderFiles });

	return (
		<Form form={form}>
			{contentTypes.includes(contentTypesConst.WAV) && (
				<ArtifactAudioPlayer
					files={sortedFiles}
					markers={markers}
					disabled={disabled}
					onAddMarker={onAddMarker}
					onUpdateMarker={onUpdateMarker}
					onRemoveMarker={onRemoveMarker}
				/>
			)}

			{contentTypes.includes(contentTypesConst.EPUB) && (
				<ArtifactEpubReader
					files={sortedFiles}
					markers={markers}
					onAddMarker={onAddMarker}
					onUpdateMarker={onUpdateMarker}
					onRemoveMarker={onRemoveMarker}
				/>
			)}

			{contentTypes.includes(contentTypesConst.WAV) && !disabled && (
				<Flex style={{ marginTop: 15 }} justifyContent="flex-start" gap={10}>
					<ConfirmationButton
						header={t("setFileChapters", "Set file chapters")}
						text={t(
							"confirmSetFileChapters",
							"Confirming this will place a chapter marker at the beginning of every file. This action will replace all other chapter markers.",
						)}
						onConfirm={() => onSetFileMarkers(sortedFiles, true)}
					>
						{t("setFileChapters", "Set file chapters")}
					</ConfirmationButton>

					<ConfirmationButton
						header={t("autoChapters", "Auto chapters")}
						text={t(
							"confirmSetAutoChapters",
							"Confirming this will place a chapter marker at every 5 second pause. This action will replace all other chapter markers.",
						)}
						onConfirm={() => onSetAutoMarkers(sortedFiles)}
					>
						{t("autoChapters", "Auto chapters")}
					</ConfirmationButton>
				</Flex>
			)}

			<Table columns={columns} data={sortedFiles} enableCheckboxes>
				{({ selectedRows }) => (
					<Flex justifyContent="flex-start" gap={10}>
						{!disabled && (
							<AsyncButton onClick={() => setShowUploadModal(true)} content={t("add", "Add")} />
						)}

						{!disabled && (
							<Button onClick={onSortFiles} disabled={isFilesSorted}>
								{t("sort", "Sort")}
							</Button>
						)}

						<Button onClick={() => setShowDownloadModal(true)} disabled={selectedRows.length === 0}>
							{t("download", "Download")}
						</Button>

						{!disabled && (
							<ConfirmationButton
								header={t("delete", "Delete")}
								text={selectedRows?.map((artifact) => artifact.name).join("\n")}
								negative={selectedRows.length > 0}
								disabled={selectedRows.length === 0}
								onConfirm={() => onDeleteFiles(selectedRows)}
							>
								{t("delete", "Delete")}
							</ConfirmationButton>
						)}

						{showDownloadModal && (
							<FileDownloadModal
								filesOrFolder={selectedRows}
								downloadFn={onDownloadFiles}
								zipDownloadFn={onDownloadFilesAsZip}
								onClose={() => setShowDownloadModal(false)}
							/>
						)}
					</Flex>
				)}
			</Table>

			<Toast open={formState.isDirty}>
				<Button onClick={onReset} disabled={!formState.isDirty || formState.isSubmitting}>
					{t("reset", "Reset")}
				</Button>

				<Button primary onClick={onSubmit} disabled={!formState.isDirty || formState.isSubmitting}>
					{t("saveChanges", "Save changes")}
				</Button>
			</Toast>

			{showUploadModal && (
				<ArtifactFilesUploadForm
					article={article}
					artifactId={artifact.id}
					onSuccess={addFiles}
					onClose={() => setShowUploadModal(false)}
				/>
			)}

			{deliveryModal?.show && (
				<ArticleDistributeModal publisher={publisher} articles={[article]} onClose={deliveryModal.onClose} />
			)}

			<Form.Blocker />
		</Form>
	);
}
